// src/pages/RegionalDashboardPage.js
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mentoringApi as axios } from '../axiosInstance';
import { ensureMentoringSession, getCachedWhoami } from '../lib/session';
import CentralGauge, { dimColor, useCountUp } from '../components/mpi/CentralGauge';
import MpiTrendModal from '../components/mpi/MpiTrendModal';

const DEV = process.env.NODE_ENV !== 'production';

function AnimatedBar({ value, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setWidth(value ?? 0), delay + 60);
    return () => clearTimeout(id);
  }, [value, delay]);
  return (
    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: 3,
        width: `${width}%`,
        background: color,
        boxShadow: `0 0 8px ${color}66`,
        transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
      }} />
    </div>
  );
}

/* dimColor + useCountUp importiert aus CentralGauge.js */

/* ─── Animated Score (Hook-safe wrapper for .map()) ─── */
function AnimatedScore({ value, color }) {
  const animated = useCountUp(value, 1200);
  return (
    <span style={{ color }}>
      {value != null ? animated.toFixed(1) : '—'}
    </span>
  );
}

/* Score-Ampel fuer NL-Karten Balken: rot < 55, orange 55-69, gruen >= 70 */
function scoreColor(v) {
  if (v == null) return '#64748B';
  if (v >= 70) return '#10B981';
  if (v >= 55) return '#F59E0B';
  return '#E24B4A';
}

/* ─── Intersection Observer: triggers once when element enters viewport ─── */
function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold: 0.2 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

/* ─── NL Card ───────────────────────────────────────────────────── */
function NLCard({ nl, regionAvg, onOpen, enterIndex = 0 }) {
  const c = nl.current ?? {};
  const p = nl.previous ?? {};
  const risk = nl.risk ?? {};
  const mpi = c.mpi_total != null ? parseFloat(c.mpi_total) : null;
  const prevMpi = p.mpi_total != null ? parseFloat(p.mpi_total) : null;
  const trend = mpi != null && prevMpi != null ? mpi - prevMpi : null;
  const col = dimColor(mpi);

  const dims = [
    { id: 'D1', label: 'Produktivität', value: (c.d1_productivity != null && c.d1_quality != null) ? parseFloat(((parseFloat(c.d1_productivity) + parseFloat(c.d1_quality)) / 2).toFixed(1)) : null },
    { id: 'D2', label: 'Ramp-Up',       value: c.d2_rampup != null ? parseFloat(c.d2_rampup) : null },
    { id: 'D3', label: 'Retention',      value: c.d3_retention != null && c.d3_risk != null ? parseFloat(((parseFloat(c.d3_retention) + parseFloat(c.d3_risk)) / 2).toFixed(1)) : c.d3_retention != null ? parseFloat(c.d3_retention) : null },
    { id: 'D4', label: 'Kultur',         value: c.d4_leadership != null ? parseFloat(c.d4_leadership) : null },
  ];

  const highRisk = risk.high ?? 0;
  const covPct = c.coverage_pct != null ? parseFloat(c.coverage_pct) : null;

  return (
    <div
      onClick={() => onOpen(nl.id)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px) scale(1.01)';
        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.borderColor = '';
      }}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 18, padding: '22px',
        display: 'flex', flexDirection: 'column', gap: 16,
        cursor: 'pointer',
        animation: `fadeInUp 0.5s ease-out ${0.15 * enterIndex}s both`,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
      }}
    >
      {/* Name + MA */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.9)', lineHeight: 1.3 }}>
            {nl.name}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 3 }}>
            {nl.staff_size || risk.total || '—'} Mitarbeiter
          </div>
        </div>
        {/* Badge nur wenn Risk-Modell aktiv (nl.risk != null). Legitime 0 HIGH bleibt sichtbar. */}
        {nl.risk && (highRisk > 0 ? (
          <div style={{
            padding: '2px 9px', borderRadius: 20, fontSize: 9, fontWeight: 600,
            background: '#EF444414', border: '1px solid #EF444440', color: '#EF4444',
            animation: 'alertPulse 2s ease-in-out infinite',
          }}>
            {highRisk} HIGH
          </div>
        ) : (
          <div style={{
            padding: '2px 9px', borderRadius: 20, fontSize: 9, fontWeight: 600,
            background: '#10B98114', border: '1px solid #10B98140', color: '#10B981',
          }}>
            0 HIGH
          </div>
        ))}
      </div>

      {/* MPI Score + Trend (animated count-up) */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        <div style={{
          fontSize: 40, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
          fontFamily: "'SF Pro Display', -apple-system, sans-serif",
        }}>
          <AnimatedScore value={mpi} color={col} />
        </div>
        {trend != null && (
          <div style={{
            fontSize: 12, fontWeight: 700, paddingBottom: 4,
            color: trend < 0 ? '#EF4444' : trend > 0 ? '#10B981' : 'rgba(255,255,255,0.3)',
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            <span style={{ fontSize: 10 }}>{trend < 0 ? '▼' : trend > 0 ? '▲' : '→'}</span>
            {trend > 0 ? '+' : ''}{trend.toFixed(1)}
          </div>
        )}
      </div>

      {/* D1-D4 Mini bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {dims.map((d, i) => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 22, fontSize: 9, color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>{d.id}</div>
            <AnimatedBar value={d.value} color={scoreColor(d.value)} delay={i * 60} />
            <div style={{ fontSize: 10, color: scoreColor(d.value), fontWeight: 600, minWidth: 26, textAlign: 'right' }}>
              {d.value != null ? d.value.toFixed(0) : '—'}
            </div>
          </div>
        ))}
      </div>

      {/* Coverage */}
      {covPct != null && (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          {c.coverage_type === 'survey' ? 'Survey-Teilnahme' : 'Abdeckung'}: <span style={{ color: covPct >= 70 ? '#10B981' : covPct >= 50 ? '#F59E0B' : '#EF4444', fontWeight: 600 }}>
            {covPct.toFixed(1)}%
          </span>{['9100', '9102'].includes(String(nl.id)) ? ' (modelliert)' : ''}
        </div>
      )}

      {/* Button */}
      <button
        onClick={(e) => { e.stopPropagation(); onOpen(nl.id); }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)';
          e.currentTarget.style.transform = 'scale(1.02)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(59,130,246,0.07)';
          e.currentTarget.style.transform = 'scale(1)';
        }}
        style={{
          width: '100%', marginTop: 'auto',
          background: 'rgba(59,130,246,0.07)',
          border: '1px solid rgba(59,130,246,0.25)',
          borderRadius: 10, color: '#3B82F6',
          fontSize: 11, fontWeight: 600, cursor: 'pointer',
          padding: '8px 12px', letterSpacing: '0.04em',
          transition: 'background 0.2s ease, transform 0.15s ease',
        }}
      >
        Dashboard öffnen →
      </button>

      {/* Massnahmen-Badge */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        paddingTop: 10,
        marginTop: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: nl.actions?.total > 0 ? '#10B981' : 'rgba(255,255,255,0.15)',
        }} />
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
          {nl.actions?.total > 0
            ? `${nl.actions.total} Maßnahme${nl.actions.total > 1 ? 'n' : ''} aktiv`
            : 'Keine Maßnahmen'}
        </span>
        {nl.actions?.total > 0 && (
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginLeft: 'auto' }}>
            {[
              nl.actions.nl > 0 ? `${nl.actions.nl} NL` : null,
              nl.actions.rl > 0 ? `${nl.actions.rl} RL` : null,
            ].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────── */
export default function RegionalDashboardPage() {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [userName, setUserName] = useState('');
  const [pulseDetail, setPulseDetail] = useState(null);
  const [showTrend, setShowTrend] = useState(false);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const whoami = await ensureMentoringSession();
        const wu = whoami?.user || whoami;
        if (alive && wu) {
          setUserName(wu.first_name || wu.firstName || wu.firstname || '');
        }
        const res = await axios.get('/mpi/region-overview', { withCredentials: true });
        if (alive) setData(res.data);
      } catch (e) {
        if (DEV) console.error('[Regional] load error:', e);
        if (alive) setError('Regionsdaten konnten nicht geladen werden.');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!pulseDetail) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setPulseDetail(null); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [pulseDetail]);

  const handleLogout = () => {
    window.location.replace('/#/nl-login');
    fetch('/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
  };

  const handleOpenNL = (tenantId) => {
    navigate(`/mpi-dashboard?tenant=${tenantId}`);
  };

  const avg = data?.region_avg ?? {};
  const mpiTotal = avg.mpi_total != null ? parseFloat(avg.mpi_total) : null;
  const previousMpi = avg.previous_mpi_total != null ? parseFloat(avg.previous_mpi_total) : null;
  const animatedMpi = useCountUp(mpiTotal);
  const dimCompRef = useRef(null);
  const dimCompVisible = useInView(dimCompRef);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#080b12', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.2em' }}>LADE REGIONSDATEN …</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: '#080b12', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div style={{ fontSize: 13, color: '#EF4444' }}>{error || 'Keine Daten verfügbar.'}</div>
        <button onClick={() => navigate(-1)} style={{
          background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8, color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: '8px 16px', fontSize: 12,
        }}>← Zurück</button>
      </div>
    );
  }

  const region = data.region ?? {};
  const period = data.period ?? '';
  const nls = data.niederlassungen ?? [];
  const mpiCol = dimColor(mpiTotal);

  const dimSummary = [
    { id: 'D1', label: 'Produktivität & Qualität', value: (avg.d1_productivity != null && avg.d1_quality != null) ? parseFloat(((parseFloat(avg.d1_productivity) + parseFloat(avg.d1_quality)) / 2).toFixed(1)) : null },
    { id: 'D2', label: 'Ramp-Up & Lernen',         value: avg.d2_rampup != null ? parseFloat(avg.d2_rampup) : null },
    { id: 'D3', label: 'Retention & Risiko',        value: avg.d3_retention != null && avg.d3_risk != null ? parseFloat(((parseFloat(avg.d3_retention) + parseFloat(avg.d3_risk)) / 2).toFixed(1)) : avg.d3_retention != null ? parseFloat(avg.d3_retention) : null },
    { id: 'D4', label: 'Niederlassungskultur',      value: avg.d4_leadership != null ? parseFloat(avg.d4_leadership) : null },
  ];

  // For Block 3: dimension comparison
  const compDims = ['d1', 'd2_rampup', 'd3', 'd4_leadership'];
  const compLabels = { d1: 'D1 Produktivität', d2_rampup: 'D2 Ramp-Up', d3: 'D3 Retention', d4_leadership: 'D4 Kultur' };
  const getCompVal = (src, key) => {
    if (key === 'd1') {
      return (src?.d1_productivity != null && src?.d1_quality != null)
        ? parseFloat(((parseFloat(src.d1_productivity) + parseFloat(src.d1_quality)) / 2).toFixed(1))
        : null;
    }
    if (key === 'd3') {
      return (src?.d3_retention != null && src?.d3_risk != null)
        ? parseFloat(((parseFloat(src.d3_retention) + parseFloat(src.d3_risk)) / 2).toFixed(1))
        : src?.d3_retention != null ? parseFloat(src.d3_retention) : null;
    }
    return src?.[key] != null ? parseFloat(src[key]) : null;
  };
  const nlColors = ['#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899'];

  // ── Region Pulse: Problem / Impact / Action insights ──
  const pulseItems = [];
  nls.forEach(nl => {
    const c = nl.current ?? {};
    const p = nl.previous ?? {};
    const shortName = (nl.name || '').replace('Ferchau ', '');
    const maCount = nl.staff_size || c.total_employees || '?';

    if (c.d2_rampup != null && parseFloat(c.d2_rampup) < 50) {
      const v = parseFloat(c.d2_rampup);
      const pv = p.d2_rampup != null ? parseFloat(p.d2_rampup) : null;
      pulseItems.push({
        type: 'alert', color: '#E24B4A', nlName: shortName, score: Math.round(v),
        problem: `Ramp-Up Score bei ${Math.round(v)} von 100`,
        impact: `Neue Mitarbeiter in ${shortName} werden langsamer produktiv. Bei ${maCount} MA wirkt sich das direkt auf den Deckungsbeitrag aus.`,
        action: 'Einarbeitungsprozesse pruefen, FK-Milestones kontrollieren',
        trend: pv != null ? v - pv : null, tenantId: nl.id,
      });
    }
    if (c.d4_leadership != null && parseFloat(c.d4_leadership) < 60) {
      const v = parseFloat(c.d4_leadership);
      const pv = p.d4_leadership != null ? parseFloat(p.d4_leadership) : null;
      pulseItems.push({
        type: 'alert', color: '#E24B4A', nlName: shortName, score: Math.round(v),
        problem: `Kulturwert bei ${Math.round(v)} von 100`,
        impact: `Fuehrungskultur und Kommunikation in ${shortName} werden von den MA kritisch bewertet. Erhoehtes Fluktuationsrisiko.`,
        action: 'Survey-Ergebnisse im Detail pruefen, Gespraech mit NL-Leitung',
        trend: pv != null ? v - pv : null, tenantId: nl.id,
      });
    }
    if (c.d3_retention != null && p.d3_retention != null) {
      const trend = parseFloat(c.d3_retention) - parseFloat(p.d3_retention);
      if (trend < -10) {
        pulseItems.push({
          type: 'warning', color: '#EF9F27', nlName: shortName, score: Math.round(parseFloat(c.d3_retention)),
          problem: `Retentions-Score um ${Math.abs(Math.round(trend))} Punkte gefallen`,
          impact: `Steigende Abwanderungsrisiken in ${shortName}. Fruehindikator fuer Kuendigungswelle in 3-6 Monaten.`,
          action: 'Risiko-Drill-Down pruefen, gefaehrdete Abteilungen identifizieren',
          trend, tenantId: nl.id,
        });
      }
    }
    if (c.coverage_pct != null && parseFloat(c.coverage_pct) < 30 && parseFloat(c.coverage_pct) >= 0) {
      const pct = parseFloat(c.coverage_pct);
      pulseItems.push({
        type: 'warning', color: '#EF9F27', nlName: shortName, score: Math.round(pct),
        problem: `Survey-Ruecklauf bei ${Math.round(pct)}%`,
        impact: `Zu wenig Datenbasis fuer belastbare Kulturwerte. D4-Score basiert auf ${pct < 5 ? 'keinen' : 'wenigen'} Antworten.`,
        action: 'Erinnerung an MA senden, NL-Leitung um Unterstuetzung bitten',
        trend: null, tenantId: nl.id,
      });
    }
    if (c.d1_productivity == null && c.mpi_total != null) {
      pulseItems.push({
        type: 'info', color: '#64748B', nlName: shortName, score: null,
        problem: 'CRM-Daten nicht verbunden',
        impact: `D1 Produktivitaet kann nicht berechnet werden. MPI-Score von ${shortName} ist unvollstaendig.`,
        action: 'CRM-Export beim Kunden anfordern',
        trend: null, tenantId: nl.id,
      });
    }
  });
  nls.forEach(nl => {
    const c = nl.current ?? {};
    const p = nl.previous ?? {};
    const shortName = (nl.name || '').replace('Ferchau ', '');
    if (p.mpi_total != null && c.mpi_total != null) {
      const delta = parseFloat(c.mpi_total) - parseFloat(p.mpi_total);
      if (delta > 3) {
        pulseItems.push({
          type: 'positive', color: '#10B981', nlName: shortName, score: Math.round(parseFloat(c.mpi_total)),
          problem: `MPI um ${delta.toFixed(1)} Punkte verbessert`,
          impact: `Positive Entwicklung in ${shortName}. Massnahmen zeigen Wirkung.`,
          action: 'Erfolg anerkennen, Best Practices auf andere NLs uebertragen',
          trend: delta, tenantId: nl.id,
        });
      }
    }
  });
  // ── Rotierende Insights (aendern sich taeglich) ──
  const _today = new Date();
  const _dayOfYear = Math.floor((_today - new Date(_today.getFullYear(), 0, 0)) / 86400000);
  const _dayOfWeek = _today.getDay();
  const _quarterEnd = new Date(_today.getFullYear(), Math.ceil((_today.getMonth() + 1) / 3) * 3, 0);
  const _daysLeft = Math.max(0, Math.ceil((_quarterEnd - _today) / 86400000));

  if (_daysLeft <= 45 && _daysLeft > 0) {
    const urg = _daysLeft <= 14 ? 'alert' : _daysLeft <= 30 ? 'warning' : 'info';
    pulseItems.push({
      type: urg, color: _daysLeft <= 14 ? '#E24B4A' : _daysLeft <= 30 ? '#EF9F27' : '#3B82F6',
      nlName: 'Quartal', score: _daysLeft,
      problem: `${_daysLeft} Tage bis Quartalsende`,
      impact: _daysLeft <= 14
        ? 'Letzte Chance fuer Survey-Ruecklauf und Massnahmen-Abschluss vor dem Quartalsreport.'
        : 'Genug Zeit fuer Korrekturen. Naechster MPI-Report am Quartalsstichtag.',
      action: _daysLeft <= 14 ? 'Offene Surveys einsammeln, Massnahmen-Status pruefen' : 'Zwischenstand pruefen, Prioritaeten setzen',
      trend: null, tenantId: null,
    });
  }

  const _dimIdx = _dayOfYear % 4;
  const _dimNames = ['Produktivitaet', 'Ramp-Up', 'Retention', 'Kultur'];
  const _dimKeys = ['d1_productivity', 'd2_rampup', 'd3_retention', 'd4_leadership'];
  const _nlsWithDim = nls.filter(nl => (nl.current ?? {})[_dimKeys[_dimIdx]] != null)
    .sort((a, b) => parseFloat((b.current ?? {})[_dimKeys[_dimIdx]] || 0) - parseFloat((a.current ?? {})[_dimKeys[_dimIdx]] || 0));
  if (_nlsWithDim.length >= 2) {
    const best = _nlsWithDim[0], worst = _nlsWithDim[_nlsWithDim.length - 1];
    const bv = parseFloat((best.current ?? {})[_dimKeys[_dimIdx]]), wv = parseFloat((worst.current ?? {})[_dimKeys[_dimIdx]]);
    const gap = Math.round(bv - wv);
    const bName = (best.name || '').replace('Ferchau ', ''), wName = (worst.name || '').replace('Ferchau ', '');
    if (gap > 5) {
      pulseItems.push({
        type: 'insight', color: '#8B5CF6', nlName: `D${_dimIdx + 1} ${_dimNames[_dimIdx]}`, score: gap,
        problem: `${gap} Punkte Spread zwischen ${bName} und ${wName}`,
        impact: `${bName} fuehrt bei ${_dimNames[_dimIdx]} (${Math.round(bv)}), ${wName} liegt bei ${Math.round(wv)}. Was macht ${bName} anders?`,
        action: 'Best Practices identifizieren und uebertragen',
        trend: null, tenantId: worst.id,
      });
    }
  }

  const _impulse = [
    { problem: 'Fruehindikator vs. Spaetindikator', impact: 'Klassische KPIs (Umsatz, DB) zeigen was PASSIERT IST. Der MPI zeigt was PASSIEREN WIRD. Ein sinkender Kulturwert heute bedeutet steigende Fluktuation in 6 Monaten.', action: 'Heute handeln statt auf den naechsten Quartalsbericht warten' },
    { problem: 'Die 90-Tage-Regel', impact: 'Die ersten 90 Tage entscheiden ob ein neuer Mitarbeiter bleibt oder geht. Ein niedriger Ramp-Up Score zeigt: Einarbeitung ist nicht strukturiert genug.', action: 'Ramp-Up Details der kritischsten NL pruefen' },
    { problem: 'Fuehrungskultur ist kein Soft-Faktor', impact: 'Gallup zeigt: 70% der Varianz im Mitarbeiter-Engagement wird durch die direkte Fuehrungskraft erklaert. D4 ist kein Nice-to-have, sondern der staerkste Hebel.', action: 'D4-Drill-Down der NL mit niedrigstem Kulturwert oeffnen' },
    { problem: 'Stille Kuendigung erkennen', impact: 'Bevor Mitarbeiter kuendigen, kuendigen sie innerlich. Sinkende Survey-Teilnahme und steigende Inaktivitaet sind Warnsignale die Monate vor der Kuendigung sichtbar werden.', action: 'D3 Retention im Detail pruefen' },
    { problem: 'Benchmark: Wo steht Ihre Region?', impact: `Ihr Regions-MPI liegt bei ${Math.round(mpiTotal || 0)}. Regionen mit MPI ueber 75 haben im Durchschnitt 23% niedrigere Fluktuation und 18% hoehere Deckungsbeitraege.`, action: 'Staerkste Dimension ausbauen, schwaechste stabilisieren' },
    { problem: 'Datenbasis staerken', impact: 'Je mehr Signalquellen aktiv sind, desto frueher erkennt der MPI Risiken. Jede fehlende Datenquelle ist ein blinder Fleck in der Frueherkennung.', action: 'Pruefen welche Datenquellen noch nicht verbunden sind' },
    { problem: 'Kleine Verbesserungen, grosse Wirkung', impact: 'Eine Verbesserung des MPI um 5 Punkte korreliert mit einem Rueckgang der ungewollten Fluktuation um ca. 12%. Jeder Punkt zaehlt.', action: 'Eine konkrete Massnahme pro NL definieren' },
  ];
  pulseItems.push({
    type: 'insight', color: '#6366F1', nlName: 'MENVIO Insight', score: null,
    ..._impulse[_dayOfYear % _impulse.length], trend: null, tenantId: null,
  });

  if (_dayOfWeek === 1) {
    pulseItems.push({ type: 'positive', color: '#10B981', nlName: 'Wochenstart', score: null,
      problem: 'Neue Woche, neuer Fokus', impact: 'Welche eine Sache wuerde den groessten Impact auf Ihre Region haben, wenn Sie sie diese Woche angehen?',
      action: 'KI-Assistent fragen: "Was sollte ich diese Woche priorisieren?"', trend: null, tenantId: null });
  } else if (_dayOfWeek === 5) {
    pulseItems.push({ type: 'positive', color: '#10B981', nlName: 'Wochenreview', score: null,
      problem: 'Freitags-Check', impact: 'Guter Zeitpunkt fuer einen kurzen Blick auf die Entwicklung der Woche. Hat sich ein Score bewegt?',
      action: 'Trend-Entwicklung aller NLs vergleichen', trend: null, tenantId: null });
  }

  const typeOrder = { alert: 0, warning: 1, insight: 2, positive: 3, info: 4 };
  pulseItems.sort((a, b) => (typeOrder[a.type] ?? 5) - (typeOrder[b.type] ?? 5));

  const _alertItems = pulseItems.filter(i => i.type === 'alert' || i.type === 'warning');
  const _insightItems = pulseItems.filter(i => i.type === 'insight' || i.type === 'positive');
  const _infoItems = pulseItems.filter(i => i.type === 'info');
  const displayItems = [..._alertItems.slice(0, 3), ..._insightItems.slice(0, 2), ..._infoItems.slice(0, 1)].slice(0, 6);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at 50% 0%, #0d1520 0%, #080b12 45%, #050709 100%)',
      fontFamily: "-apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif",
      color: 'white',
      padding: '28px 32px 40px',
    }}>
      <style>{`
        @keyframes mpiPulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes alertPulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes scaleIn { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }
        * { box-sizing:border-box; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, maxWidth: 1100, margin: '0 auto 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, boxShadow: '0 4px 14px #3B82F644',
          }}>◎</div>
          <div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.28em', textTransform: 'uppercase' }}>
              MENVIO Performance Index
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px' }}>
              {region.name || 'Region'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
              Region · {nls.length} Niederlassung{nls.length !== 1 ? 'en' : ''} · {avg.total_employees ?? 0} Mitarbeiter
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7,
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
            borderRadius: 9, padding: '7px 13px',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981', boxShadow: '0 0 7px #10B981' }} />
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>{period}</span>
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('open-support-chat', {
              detail: { role: 'regional_manager', firstName: userName, tenantName: region.name || '', tenantId: '' }
            }))}
            style={{
              padding: '7px 14px',
              background: 'linear-gradient(90deg, #9AF68C, #7EE87E)',
              border: 'none',
              borderRadius: 9,
              color: '#0b0f0a',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.03em',
            }}
          >
            KI-Assistent
          </button>
          <button
            onClick={handleLogout}
            style={{
              padding: '7px 14px', background: 'transparent',
              border: '1px solid rgba(255,255,255,0.15)', borderRadius: 9,
              color: 'rgba(255,255,255,0.45)', fontSize: 11, cursor: 'pointer',
              letterSpacing: '0.03em',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'}
          >
            Abmelden
          </button>
        </div>
      </div>

      {/* ── Block 1: CentralGauge Hero Ring ── */}
      <div style={{
        maxWidth: 1100, margin: '0 auto 28px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '1rem 0 1.5rem',
      }}>
        <div
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.015)';
            e.currentTarget.style.boxShadow = '0 32px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.12)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.09)';
          }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(160deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 28, padding: '32px 22px 28px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.09)',
            backdropFilter: 'blur(20px)',
            position: 'relative', overflow: 'hidden', gap: 16,
            animation: 'scaleIn 0.6s ease-out both',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          }}
        >
          <div style={{
            position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
            width: 260, height: 260, borderRadius: '50%',
            background: 'radial-gradient(#10B98110, transparent 70%)',
            pointerEvents: 'none',
          }} />
          {/* Ring + D1-D4 Labels */}
          <div style={{ position: 'relative', width: 280, height: 280 }}>
            <div style={{ position: 'absolute', top: 20, left: 20, width: 240, height: 240 }}>
              <CentralGauge
                score={mpiTotal ?? 0}
                prev={previousMpi}
                label="REGIONS-MPI"
                segmentColors={[
                  scoreColor(dimSummary[0]?.value),
                  scoreColor(dimSummary[1]?.value),
                  scoreColor(dimSummary[2]?.value),
                  scoreColor(dimSummary[3]?.value),
                ]}
              />
            </div>
            {/* D1: oben-links */}
            <div title="D1 Produktivität & Qualität" style={{ position: 'absolute', top: 0, left: 0, textAlign: 'center', cursor: 'help' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.05em' }}>D1</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: scoreColor(dimSummary[0]?.value), marginTop: 1 }}>
                {dimSummary[0]?.value != null ? Math.round(dimSummary[0].value) : '\u2014'}
              </div>
            </div>
            {/* D2: oben-rechts */}
            <div title="D2 Ramp-Up & Lernen" style={{ position: 'absolute', top: 0, right: 0, textAlign: 'center', cursor: 'help' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.05em' }}>D2</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: scoreColor(dimSummary[1]?.value), marginTop: 1 }}>
                {dimSummary[1]?.value != null ? Math.round(dimSummary[1].value) : '\u2014'}
              </div>
            </div>
            {/* D3: unten-links */}
            <div title="D3 Retention & Risiko" style={{ position: 'absolute', bottom: 0, left: 0, textAlign: 'center', cursor: 'help' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.05em' }}>D3</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: scoreColor(dimSummary[2]?.value), marginTop: 1 }}>
                {dimSummary[2]?.value != null ? Math.round(dimSummary[2].value) : '\u2014'}
              </div>
            </div>
            {/* D4: unten-rechts */}
            <div title="D4 Niederlassungskultur" style={{ position: 'absolute', bottom: 0, right: 0, textAlign: 'center', cursor: 'help' }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.05em' }}>D4</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: scoreColor(dimSummary[3]?.value), marginTop: 1 }}>
                {dimSummary[3]?.value != null ? Math.round(dimSummary[3].value) : '\u2014'}
              </div>
            </div>
          </div>
          <button
            type="button"
            aria-label="Verlauf anzeigen"
            onClick={() => setShowTrend(true)}
            style={{
              background: 'rgba(46,117,182,0.10)',
              border: '1px solid rgba(46,117,182,0.35)',
              borderRadius: 8, color: '#8ab4e8',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              padding: '5px 12px', letterSpacing: '0.03em',
            }}
          >
            Verlauf anzeigen ↗
          </button>
          <p style={{
            fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', margin: 0, textAlign: 'center',
            animation: 'fadeIn 0.5s ease-out 0.8s both',
          }}>
            {nls.length} Niederlassung{nls.length !== 1 ? 'en' : ''} · {avg.total_employees ?? 0} Mitarbeiter · {period}
          </p>
        </div>
      </div>

      {/* ── Block 1 S9 (Bars + Risk Widget auskommentiert für Rollback) ──
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {dimSummary.map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 160, fontSize: 11, color: 'rgba(255,255,255,0.5)', display: 'flex', gap: 6 }}>
                <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 600 }}>{d.id}</span>
                <span>{d.label}</span>
              </div>
              <AnimatedBar value={d.value} color={dimColor(d.value)} delay={i * 80} />
              <div style={{
                fontSize: 14, fontWeight: 800, minWidth: 36, textAlign: 'right',
                color: dimColor(d.value), letterSpacing: '-0.5px',
              }}>
                {d.value != null ? d.value.toFixed(1) : '\u2014'}
              </div>
            </div>
          ))}
        </div>

        <div style={{
          flexShrink: 0, padding: '16px 20px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 14, textAlign: 'center', minWidth: 120,
        }}>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            Ø Risiko
          </div>
          <div style={{
            fontSize: 28, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1,
            color: dimColor(100 - (avg.avg_risk_score ?? 50)),
            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
          }}>
            {avg.avg_risk_score != null ? parseFloat(avg.avg_risk_score).toFixed(1) : '\u2014'}
          </div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}>
            {avg.total_employees ?? 0} MA
          </div>
        </div>
      ── Ende Block 1 S9 Rollback */}

      {/* ── Block 1 OLD (auskommentiert für Rollback) ──
      <div style={{
        maxWidth: 1100, margin: '0 auto 28px',
        background: 'linear-gradient(160deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 22, padding: '28px 32px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.09)',
        backdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'center', gap: 40,
      }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.28em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 6 }}>
            Regions-MPI
          </div>
          <div style={{
            fontSize: 56, fontWeight: 800, lineHeight: 1, letterSpacing: '-3px',
            color: mpiCol,
            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
            textShadow: `0 0 32px ${mpiCol}55`,
          }}>
            {animatedMpi.toFixed(1)}
          </div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}></div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {dimSummary.map((d, i) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 160, fontSize: 11, color: 'rgba(255,255,255,0.5)', display: 'flex', gap: 6 }}>
                <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 600 }}>{d.id}</span>
                <span>{d.label}</span>
              </div>
              <AnimatedBar value={d.value} color={dimColor(d.value)} delay={i * 80} />
              <div style={{
                fontSize: 14, fontWeight: 800, minWidth: 36, textAlign: 'right',
                color: dimColor(d.value), letterSpacing: '-0.5px',
              }}>
                {d.value != null ? d.value.toFixed(1) : '—'}
              </div>
            </div>
          ))}
        </div>
        <div style={{
          flexShrink: 0, padding: '16px 20px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 14, textAlign: 'center', minWidth: 120,
        }}>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 8 }}>
            Ø Risiko
          </div>
          <div style={{
            fontSize: 28, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1,
            color: dimColor(100 - (avg.avg_risk_score ?? 50)),
            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
          }}>
            {avg.avg_risk_score != null ? parseFloat(avg.avg_risk_score).toFixed(1) : '—'}
          </div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', marginTop: 4 }}>
            {avg.total_employees ?? 0} MA
          </div>
        </div>
      </div>
      */}

      {/* ── Block 2: Niederlassungs-Vergleich ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto 28px' }}>
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16, animation: 'fadeIn 0.4s ease-out 0.1s both' }}>
          Niederlassungen
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(nls.length, 3)}, 1fr)`,
          gap: 16,
        }}>
          {nls.map((nl, i) => (
            <NLCard key={nl.id ?? i} nl={nl} regionAvg={avg} onOpen={handleOpenNL} enterIndex={i} />
          ))}
        </div>
      </div>

      {/* ── Region Pulse (Problem / Impact / Action) ── */}
      {displayItems.length > 0 && (
        <div style={{ maxWidth: 1100, margin: '0 auto 28px', animation: 'fadeInUp 0.5s ease-out 0.6s both' }}>
          <div style={{
            fontSize: 9, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.35)',
            textTransform: 'uppercase', marginBottom: 14,
          }}>
            Region Pulse
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {displayItems.map((item, i) => (
              <div
                key={i}
                onClick={() => setPulseDetail(item)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.008)';
                  e.currentTarget.style.boxShadow = `0 12px 32px rgba(0,0,0,0.4), 0 0 0 1px ${item.color}22`;
                  e.currentTarget.style.borderColor = item.color + '44';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)';
                }}
                style={{
                  position: 'relative',
                  display: 'grid', gridTemplateColumns: '56px 1fr', gap: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14, overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                  animation: `fadeInUp 0.4s ease-out ${0.1 * i + 0.7}s both`,
                }}
              >
                {/* Left: Score or Icon */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '16px 8px', background: item.color + '12', borderRight: `1px solid ${item.color}22`,
                }}>
                  {item.score != null ? (
                    <span style={{ fontSize: 22, fontWeight: 700, color: item.color, lineHeight: 1 }}>
                      {item.score}
                    </span>
                  ) : (
                    <span style={{ fontSize: 20, color: item.color, opacity: 0.7 }}>
                      {item.type === 'insight' ? '\u25C6' : item.type === 'positive' ? '\u2726' : '\u25CB'}
                    </span>
                  )}
                  {item.trend != null && (
                    <span style={{ fontSize: 10, color: item.trend < 0 ? '#EF4444' : '#10B981', marginTop: 2 }}>
                      {item.trend >= 0 ? '\u25B2' : '\u25BC'}{Math.abs(item.trend).toFixed(0)}
                    </span>
                  )}
                </div>
                {/* Right: Problem / Impact / Action */}
                <div style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.03em' }}>
                      {item.nlName}
                    </span>
                    <span style={{
                      fontSize: 9, padding: '2px 8px', borderRadius: 6,
                      background: item.color + '20', color: item.color,
                      textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em',
                    }}>
                      {item.type === 'alert' ? 'Kritisch' : item.type === 'warning' ? 'Achtung' : item.type === 'insight' ? 'Insight' : item.type === 'positive' ? 'Positiv' : 'Info'}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.9)', marginBottom: 4, lineHeight: 1.3 }}>
                    {item.problem}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 6, lineHeight: 1.4 }}>
                    {item.impact}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: item.color, opacity: 0.8 }}>{'\u2192'}</span>
                    <span style={{ fontSize: 11, color: item.color, opacity: 0.8 }}>{item.action}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Pulse Detail Modal ── */}
      {pulseDetail && (() => {
        const nl = nls.find(n => n.id === pulseDetail.tenantId);
        const nlC = nl?.current ?? {};
        const nlDims = nl ? [
          { id: 'D1', label: 'Produktivität', value: (nlC.d1_productivity != null && nlC.d1_quality != null) ? parseFloat(((parseFloat(nlC.d1_productivity) + parseFloat(nlC.d1_quality)) / 2).toFixed(1)) : null },
          { id: 'D2', label: 'Ramp-Up', value: nlC.d2_rampup != null ? parseFloat(nlC.d2_rampup) : null },
          { id: 'D3', label: 'Retention', value: nlC.d3_retention != null ? parseFloat(nlC.d3_retention) : null },
          { id: 'D4', label: 'Kultur', value: nlC.d4_leadership != null ? parseFloat(nlC.d4_leadership) : null },
        ] : [];
        const typeBadge = { alert: 'Kritisch', warning: 'Achtung', insight: 'Insight', positive: 'Positiv', info: 'Info' };
        return (
          <div
            onClick={() => setPulseDetail(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'fadeIn 0.2s ease-out',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '90%', maxWidth: 520,
                background: 'linear-gradient(145deg, #141a26 0%, #0d1117 100%)',
                border: `1px solid ${pulseDetail.color}33`,
                borderRadius: 20, padding: '28px 28px 22px',
                animation: 'scaleIn 0.25s ease-out',
                maxHeight: '90vh', overflowY: 'auto',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <span style={{
                    fontSize: 9, padding: '3px 10px', borderRadius: 6,
                    background: pulseDetail.color + '20', color: pulseDetail.color,
                    textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.08em',
                  }}>
                    {typeBadge[pulseDetail.type] || pulseDetail.type}
                  </span>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
                    {pulseDetail.nlName}
                  </div>
                </div>
                {pulseDetail.score != null && (
                  <div style={{
                    fontSize: 36, fontWeight: 800, color: pulseDetail.color, lineHeight: 1,
                    fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                    textShadow: `0 0 24px ${pulseDetail.color}44`,
                  }}>
                    {pulseDetail.score}
                  </div>
                )}
              </div>

              {/* Problem */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 4 }}>
                  Problem
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.92)', lineHeight: 1.4 }}>
                  {pulseDetail.problem}
                </div>
              </div>

              {/* Impact */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 4 }}>
                  Impact
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                  {pulseDetail.impact}
                </div>
              </div>

              {/* Action */}
              <div style={{
                marginBottom: nl ? 20 : 16,
                padding: '12px 14px', borderRadius: 10,
                background: pulseDetail.color + '10',
                borderLeft: `3px solid ${pulseDetail.color}66`,
              }}>
                <div style={{ fontSize: 9, letterSpacing: '0.12em', color: pulseDetail.color, textTransform: 'uppercase', marginBottom: 4, opacity: 0.7 }}>
                  Empfohlene Aktion
                </div>
                <div style={{ fontSize: 13, color: pulseDetail.color, fontWeight: 500, lineHeight: 1.4 }}>
                  {pulseDetail.action}
                </div>
              </div>

              {/* NL D1-D4 Overview (only if tenantId exists) */}
              {nl && nlDims.length > 0 && (
                <div style={{
                  padding: '14px 16px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  marginBottom: 18,
                }}>
                  <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 10 }}>
                    {(nl.name || '').replace('Ferchau ', '')} — Dimensionen
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {nlDims.map(d => (
                      <div key={d.id} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>{d.id}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: scoreColor(d.value), lineHeight: 1 }}>
                          {d.value != null ? Math.round(d.value) : '\u2014'}
                        </div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{d.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer buttons */}
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                {/* Primary: Massnahme anlegen */}
                <div
                  onClick={() => {
                    const nlName = pulseDetail.nlName || 'der Niederlassung';
                    const ctx = pulseDetail.type === 'alert' || pulseDetail.type === 'warning'
                      ? `Fuer ${nlName} liegt folgendes Problem vor: ${pulseDetail.problem}. ${pulseDetail.impact} Bitte schlage mir eine konkrete Massnahme vor, die ich als Regionalleiter umsetzen kann. Beruecksichtige dabei die aktuelle Datenlage.`
                      : pulseDetail.type === 'insight'
                        ? `Ich moechte zum folgenden Thema eine Massnahme definieren: ${pulseDetail.problem}. ${pulseDetail.impact}`
                        : `Bitte schlage mir eine Massnahme vor fuer: ${pulseDetail.problem}`;
                    setPulseDetail(null);
                    window.dispatchEvent(new CustomEvent('open-support-chat', { detail: { message: ctx, tenantId: '' } }));
                  }}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: 10, textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(99,102,241,0.08) 100%)',
                    border: '1px solid rgba(99,102,241,0.3)',
                    fontSize: 13, fontWeight: 500, color: '#818CF8',
                    cursor: 'pointer', transition: 'background 0.15s, transform 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(99,102,241,0.15) 100%)'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(99,102,241,0.08) 100%)'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  Maßnahme anlegen →
                </div>
                {/* Secondary: NL-Dashboard */}
                {pulseDetail.tenantId && (
                  <div
                    onClick={() => { setPulseDetail(null); navigate(`/mpi-dashboard?tenant=${pulseDetail.tenantId}`); }}
                    style={{
                      padding: '10px 16px', borderRadius: 10,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 500,
                      cursor: 'pointer', transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                  >
                    NL-Dashboard →
                  </div>
                )}
                {/* Tertiary: Schliessen */}
                <div
                  onClick={() => setPulseDetail(null)}
                  style={{
                    padding: '10px 16px', borderRadius: 10,
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                    color: 'rgba(255,255,255,0.35)', fontSize: 12, fontWeight: 500,
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                >
                  Schließen
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Block 3: Dimensions-Vergleich (scroll-triggered) ── */}
      {nls.length > 1 && (
        <div ref={dimCompRef} style={{
          maxWidth: 1100, margin: '0 auto 28px', padding: '24px 28px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 18,
          opacity: dimCompVisible ? 1 : 0,
          transform: dimCompVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
        }}>
          <div style={{
            fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6,
            animation: dimCompVisible ? 'fadeIn 0.4s ease-out both' : 'none',
            opacity: dimCompVisible ? undefined : 0,
          }}>
            Dimensions-Vergleich
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
            {nls.map((nl, i) => (
              <div key={nl.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: nlColors[i % nlColors.length] }} />
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>{nl.name}</span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 16, height: 2, background: 'rgba(255,255,255,0.25)', borderRadius: 1 }} />
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>Regions-Ø</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${compDims.length}, 1fr)`, gap: 24 }}>
            {compDims.map((dimKey, di) => {
              const avgVal = getCompVal(avg, dimKey);
              return (
                <div key={dimKey}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 600, marginBottom: 12 }}>
                    {compLabels[dimKey]}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {nls.map((nl, i) => {
                      const val = getCompVal(nl.current, dimKey);
                      const barDelay = di * 0.1 + i * 0.05;
                      return (
                        <div key={nl.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: nlColors[i % nlColors.length], flexShrink: 0,
                          }} />
                          <div style={{
                            flex: 1, height: 10, borderRadius: 5,
                            background: 'rgba(255,255,255,0.05)',
                            position: 'relative', overflow: 'hidden',
                          }}>
                            <div style={{
                              height: '100%', borderRadius: 5,
                              width: val != null ? `${val}%` : '0%',
                              background: nlColors[i % nlColors.length],
                              boxShadow: `0 0 6px ${nlColors[i % nlColors.length]}66`,
                              transformOrigin: 'left',
                              transform: dimCompVisible ? 'scaleX(1)' : 'scaleX(0)',
                              transition: `transform 0.8s ease-out ${barDelay}s`,
                            }} />
                            {/* Region average marker */}
                            {avgVal != null && (
                              <div style={{
                                position: 'absolute', top: 0, bottom: 0,
                                left: `${avgVal}%`,
                                width: 2, background: 'rgba(255,255,255,0.3)',
                                borderRadius: 1,
                              }} />
                            )}
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: dimColor(val), minWidth: 28, textAlign: 'right' }}>
                            {val != null ? val.toFixed(0) : '—'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showTrend && <MpiTrendModal tenant={null} period={period} label="Regions-MPI-Verlauf" onClose={() => setShowTrend(false)} />}

      {/* ── Footer ── */}
      <div style={{
        maxWidth: 1100, margin: '0 auto',
        padding: '10px 14px',
        background: 'rgba(255,255,255,0.02)',
        borderRadius: 9, borderLeft: '3px solid #3B82F6',
        fontSize: 11, color: 'rgba(255,255,255,0.25)', lineHeight: 1.6,
      }}>
        Regionsübersicht · {nls.length} Niederlassung{nls.length !== 1 ? 'en' : ''} · {avg.total_employees ?? 0} Mitarbeiter · {period}
      </div>
    </div>
  );
}
